import type {
  MappedTuitionYear,
  NormalizedTuitionEstimateInput,
  NormalizedTuitionRequestInput,
  TuitionEstimateApiResponse,
  TuitionEstimateBuildResult,
  TuitionEstimateErrorCode,
  TuitionEstimateInput,
  TuitionFeeRowSource,
  TuitionSemesterItem,
  TuitionSemesterSource,
  TuitionYearEstimate,
  TuitionYearSource
} from "~/types/types";

const REQUEST_STRING_FIELDS = [
  "program_code",
  "residency_status",
  "degree_type",
  "fee_code"
] as const;

const NONNEGATIVE_NUMBER_FIELDS = [
  "total_credits",
  "transfer_credits",
  "grants",
  "scholarships",
  "employer_tuition_benefit"
] as const;

export class TuitionEstimateDomainError extends Error {
  readonly code: TuitionEstimateErrorCode;

  constructor(code: TuitionEstimateErrorCode, message: string) {
    super(message);
    this.name = "TuitionEstimateDomainError";
    this.code = code;
  }
}

const normalizeAcademicYear = (
  value: number | string,
  fieldName: "costAcademicYear" | "latestAcademicYear"
): number => {
  const year = Number(typeof value === "string" ? value.trim() : value);

  if (!Number.isInteger(year) || year <= 0) {
    throw new TuitionEstimateDomainError("validation", `${fieldName} must be a positive integer`);
  }

  return year;
};

const requireNonnegativeFiniteNumber = (value: number, fieldName: string): number => {
  if (!Number.isFinite(value) || value < 0) {
    throw new TuitionEstimateDomainError(
      "validation",
      `${fieldName} must be a finite nonnegative number`
    );
  }

  return value;
};

export const normalizeAcademicYears = (
  costAcademicYear: number | string,
  latestAcademicYear: number | string
): number[] => {
  const costYear = normalizeAcademicYear(costAcademicYear, "costAcademicYear");
  const latestYear = normalizeAcademicYear(latestAcademicYear, "latestAcademicYear");

  return costYear === latestYear ? [costYear] : [costYear, latestYear];
};

export const normalizeTuitionRequestInput = (
  input: TuitionEstimateInput
): NormalizedTuitionRequestInput => {
  const requestInput: NormalizedTuitionRequestInput = {
    costAcademicYear: normalizeAcademicYear(input.costAcademicYear, "costAcademicYear"),
    latestAcademicYear: normalizeAcademicYear(input.latestAcademicYear, "latestAcademicYear"),
    program_code: "",
    residency_status: "",
    degree_type: "",
    fee_code: ""
  };

  for (const field of REQUEST_STRING_FIELDS) {
    const value = input[field].trim();

    if (field !== "fee_code" && value.length === 0) {
      throw new TuitionEstimateDomainError("validation", `${field} is required`);
    }

    requestInput[field] = value;
  }

  return requestInput;
};

export const normalizeTuitionEstimateInput = (
  input: TuitionEstimateInput
): NormalizedTuitionEstimateInput => {
  const requestInput = normalizeTuitionRequestInput(input);
  const normalizedNumbers = {} as Pick<
    NormalizedTuitionEstimateInput,
    (typeof NONNEGATIVE_NUMBER_FIELDS)[number]
  >;

  for (const field of NONNEGATIVE_NUMBER_FIELDS) {
    normalizedNumbers[field] = requireNonnegativeFiniteNumber(input[field], field);
  }

  if (
    !Number.isInteger(input.credits_per_semester) ||
    input.credits_per_semester < 1 ||
    input.credits_per_semester > 18
  ) {
    throw new TuitionEstimateDomainError(
      "validation",
      "credits_per_semester must be an integer from 1 through 18"
    );
  }

  const includeSummer = input.include_summer ?? true;

  if (typeof includeSummer !== "boolean") {
    throw new TuitionEstimateDomainError("validation", "include_summer must be a boolean");
  }

  return {
    ...requestInput,
    ...normalizedNumbers,
    credits_per_semester: input.credits_per_semester,
    include_summer: includeSummer
  };
};

export const createTuitionRequestSignature = (input: TuitionEstimateInput): string => {
  const request = normalizeTuitionRequestInput(input);

  return JSON.stringify([
    request.costAcademicYear,
    request.latestAcademicYear,
    request.program_code,
    request.residency_status,
    request.degree_type,
    request.fee_code
  ]);
};

export const buildTuitionCostUrl = (
  apiBaseUrl: string,
  apiCostInfoPath: string,
  input: NormalizedTuitionRequestInput,
  academicYear: number
): string => {
  const params = new URLSearchParams([
    ["honors", "0"],
    ["admit_term", ""],
    ["include_summer", "0"],
    ["campus", "ONLNE"],
    ["acad_year", academicYear.toString()],
    ["acad_prog", input.program_code],
    ["residency", input.residency_status],
    ["acad_career", input.degree_type],
    ["program_fee", input.fee_code]
  ]);

  return `${apiBaseUrl}${apiCostInfoPath}?${params.toString()}`;
};

const CREDIT_KEYS = Array.from({ length: 18 }, (_, index) => (index + 1).toString());

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseFiniteAmount = (value: unknown, context: string): number => {
  if (
    (typeof value !== "string" && typeof value !== "number") ||
    (typeof value === "string" && value.trim() === "") ||
    !Number.isFinite(Number(value))
  ) {
    throw new TuitionEstimateDomainError(
      "response",
      `${context} must contain a finite numeric value`
    );
  }

  return Number(value);
};

const cloneRow = (row: TuitionFeeRowSource): TuitionFeeRowSource => ({
  ...row
});

const validateRequiredSemester = (
  value: unknown,
  semesterName: "fall" | "spring"
): TuitionSemesterSource => {
  if (!isRecord(value)) {
    throw new TuitionEstimateDomainError("response", `${semesterName} breakdown is missing`);
  }

  const entries = Object.entries(value);

  if (entries.length < 2 || !isRecord(value.total)) {
    throw new TuitionEstimateDomainError(
      "response",
      `${semesterName} breakdown must contain fee rows and a total row`
    );
  }

  const semester: TuitionSemesterSource = {};

  for (const [code, rawRow] of entries) {
    if (!isRecord(rawRow) || typeof rawRow.descr !== "string") {
      throw new TuitionEstimateDomainError(
        "response",
        `${semesterName}.${code} must contain a description`
      );
    }

    const descr = rawRow.descr.trim();

    if (descr.length === 0) {
      throw new TuitionEstimateDomainError(
        "response",
        `${semesterName}.${code} must contain a description`
      );
    }

    for (const creditKey of CREDIT_KEYS) {
      parseFiniteAmount(rawRow[creditKey], `${semesterName}.${code}.${creditKey}`);
    }

    semester[code] = cloneRow(rawRow as TuitionFeeRowSource);
    semester[code].descr = descr;
  }

  return semester;
};

const cloneOptionalSemester = (value: unknown): TuitionSemesterSource | undefined => {
  if (!isRecord(value)) return undefined;

  const semester: TuitionSemesterSource = {};

  for (const [code, rawRow] of Object.entries(value)) {
    if (!isRecord(rawRow) || typeof rawRow.descr !== "string") {
      return undefined;
    }

    const descr = rawRow.descr.trim();

    if (descr.length === 0) return undefined;

    semester[code] = cloneRow(rawRow as TuitionFeeRowSource);
    semester[code].descr = descr;
  }

  return semester;
};

export const normalizeTuitionYearSource = (
  academicYear: number,
  response: TuitionEstimateApiResponse
): TuitionYearSource => {
  if (
    !isRecord(response) ||
    typeof response.status !== "number" ||
    !Number.isFinite(response.status)
  ) {
    throw new TuitionEstimateDomainError(
      "response",
      `Tuition response for ${academicYear} is malformed`
    );
  }

  if (response.status < 200 || response.status >= 300) {
    throw new TuitionEstimateDomainError("api", `Tuition API returned status ${response.status}`);
  }

  const breakdown = response.data?.breakdown;

  if (!breakdown) {
    throw new TuitionEstimateDomainError(
      "response",
      `Tuition response for ${academicYear} is missing breakdown data`
    );
  }

  return {
    academic_year: academicYear,
    fall: validateRequiredSemester(breakdown.fall, "fall"),
    spring: validateRequiredSemester(breakdown.spring, "spring"),
    summer: cloneOptionalSemester(breakdown.summer)
  };
};

const mapSemesterAtCredit = (
  semester: TuitionSemesterSource,
  creditKey: string,
  semesterName: "fall" | "spring" | "summer"
): {
  items: TuitionSemesterItem[];
  numericTotal: number;
  stringTotal: string;
} => {
  const totalRow = semester.total;

  if (!totalRow) {
    throw new TuitionEstimateDomainError("response", `${semesterName} total row is missing`);
  }

  const items = Object.entries(semester)
    .filter(([code]) => code !== "total")
    .map(([code, row]) => {
      parseFiniteAmount(row[creditKey], `${semesterName}.${code}.${creditKey}`);

      return {
        descr: row.descr,
        value: String(row[creditKey])
      };
    });

  if (items.length === 0) {
    throw new TuitionEstimateDomainError(
      "response",
      `${semesterName} must contain at least one fee row`
    );
  }

  const numericTotal = parseFiniteAmount(totalRow[creditKey], `${semesterName}.total.${creditKey}`);
  const stringTotal = String(totalRow[creditKey]);

  items.push({
    descr: "Total Tuition & Fees",
    value: stringTotal
  });

  return { items, numericTotal, stringTotal };
};

const canMapSemesterAtCredit = (
  semester: TuitionSemesterSource | undefined,
  creditKey: string
): semester is TuitionSemesterSource => {
  if (!semester) return false;

  try {
    mapSemesterAtCredit(semester, creditKey, "summer");
    return true;
  } catch {
    return false;
  }
};

export const mapTuitionYearSemesters = (
  source: TuitionYearSource,
  creditsPerSemester: number,
  includeSummer: boolean
): MappedTuitionYear => {
  const creditKey = creditsPerSemester.toString();
  const fall = mapSemesterAtCredit(source.fall, creditKey, "fall");
  const spring = mapSemesterAtCredit(source.spring, creditKey, "spring");
  const mapped: MappedTuitionYear = {
    semesters: {
      fall: fall.items,
      spring: spring.items
    },
    numericTotals: {
      fall: fall.numericTotal,
      spring: spring.numericTotal
    },
    stringTotals: {
      fall: fall.stringTotal,
      spring: spring.stringTotal
    }
  };

  if (includeSummer) {
    const summerSource = canMapSemesterAtCredit(source.summer, creditKey)
      ? source.summer
      : source.spring;
    const summer = mapSemesterAtCredit(summerSource, creditKey, "summer");

    mapped.semesters.summer = summer.items.map((item) => ({ ...item }));
    mapped.numericTotals.summer = summer.numericTotal;
    mapped.stringTotals.summer = summer.stringTotal;
  }

  return mapped;
};

const estimateNumberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
});

export const formatEstimateNumber = (value: number): string =>
  estimateNumberFormatter.format(value);

const roundToOneDecimal = (value: number): number => Math.round((value + Number.EPSILON) * 10) / 10;

export const buildTuitionEstimates = (
  sources: TuitionYearSource[],
  input: TuitionEstimateInput
): TuitionEstimateBuildResult => {
  const normalized = normalizeTuitionEstimateInput(input);
  const remainingCredits = Math.max(normalized.total_credits - normalized.transfer_credits, 0);
  const termsPerYear = normalized.include_summer ? 3 : 2;
  const completionTime = roundToOneDecimal(
    remainingCredits / normalized.credits_per_semester / termsPerYear
  );
  const savingsApplied =
    normalized.grants + normalized.scholarships + normalized.employer_tuition_benefit;
  const result: TuitionEstimateBuildResult = {
    estimates: [],
    semesterTotals: {}
  };

  for (const source of sources) {
    const mapped = mapTuitionYearSemesters(
      source,
      normalized.credits_per_semester,
      normalized.include_summer
    );
    let academicYearTotal = mapped.numericTotals.fall! + mapped.numericTotals.spring!;

    if (normalized.include_summer) {
      academicYearTotal += mapped.numericTotals.summer!;
    }

    const estimatedAcademicYearTotal = Math.max(academicYearTotal - savingsApplied, 0);
    const estimate: TuitionYearEstimate = {
      academic_year: source.academic_year,
      fall: mapped.semesters.fall,
      spring: mapped.semesters.spring,
      academic_year_total: formatEstimateNumber(academicYearTotal),
      savings_applied: formatEstimateNumber(savingsApplied),
      estimated_academic_year_total: formatEstimateNumber(estimatedAcademicYearTotal),
      completion_time: completionTime,
      estimated_total_cost: formatEstimateNumber(estimatedAcademicYearTotal * completionTime)
    };

    if (normalized.include_summer && mapped.semesters.summer) {
      estimate.summer = mapped.semesters.summer;
    }

    result.estimates.push(estimate);
    result.semesterTotals[source.academic_year.toString()] = {
      ...mapped.stringTotals
    };
  }

  return result;
};
